<script setup>
  import Button from 'primevue/button'
  import Message from 'primevue/message'
  import { useRoute } from 'vue-router'

  import { computed } from 'vue'

  import MainLayout from '@/features/layout/MainLayout.vue'
  import { useClubRouter } from '@/features/router/useClubRouer.js'

  const route = useRoute()
  const router = useClubRouter()
  const reason = computed(() => {
    const rawValue = route.query.reason
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue
    return typeof value === 'string' ? value : null
  })
  const redirectUrl = computed(() => {
    const rawValue = route.query.redirectUrl
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue

    if (typeof value !== 'string' || !value) {
      return null
    }

    try {
      const parsed = new URL(value, window.location.origin)

      if (parsed.origin !== window.location.origin) {
        return null
      }

      if (parsed.pathname.includes('/error')) {
        return '/'
      }

      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    } catch {
      return null
    }
  })

  const errorMessages = {
    'network-error': {
      icon: 'pi pi-wifi',
      title: 'Network Error',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
    },
    'auth-error': {
      icon: 'pi pi-exclamation-triangle',
      title: 'Authentication Error',
      message: 'There was an authentication error. Please try logging in again.',
    },
  }

  const errorConfig = computed(() => {
    return (
      errorMessages[reason.value] || {
        icon: 'pi pi-times-circle',
        title: 'Something Went Wrong',
        message: 'An unexpected error occurred. Please try again later.',
      }
    )
  })

  function goToHome() {
    router.push('/')
  }

  function goBack() {
    router.back()
  }

  function tryAgain() {
    if (redirectUrl.value) {
      window.location.assign(redirectUrl.value)
      return
    }

    window.location.reload()
  }
</script>

<template>
  <MainLayout :show-sidebar="false">
    <div class="error-view">
      <div class="error-container">
        <div class="error-icon">
          <i :class="errorConfig.icon" style="font-size: 40px"></i>
        </div>

        <h1 class="error-title">{{ errorConfig.title }}</h1>

        <Message severity="error" class="error-message">
          <p class="error-text">{{ errorConfig.message }}</p>
          <p v-if="reason && !errorMessages[reason]" class="error-reason">Reason: {{ reason }}</p>
        </Message>

        <div class="error-actions">
          <Button
            v-if="reason === 'network-error' || redirectUrl"
            severity="primary"
            type="button"
            aria-label="Try Again"
            @click="tryAgain"
          >
            Try Again
          </Button>
          <Button severity="primary" type="button" aria-label="Go to Homepage" @click="goToHome">
            Go to Homepage
          </Button>
          <Button severity="secondary" type="button" aria-label="Go Back" @click="goBack">
            Go Back
          </Button>
        </div>

        <p class="error-help">Need help? Please contact your administrator or support team.</p>
      </div>
    </div>
  </MainLayout>
</template>

<style lang="scss" scoped>
  .error-view {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 70vh;
    padding: 2rem;
  }

  .error-container {
    max-width: 550px;
    width: 100%;
    text-align: center;
    background-color: var(--surface-card, #ffffff);
    border-radius: 12px;
    padding: 3rem 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .error-icon {
    font-size: 40px;
    color: var(--error-color, #e24c4c);
    margin-bottom: 1.5rem;
  }

  .error-title {
    font-size: 2rem;
    font-weight: 600;
    color: var(--text-color, #333333);
    margin-bottom: 1.5rem;
  }

  .error-message {
    width: 100%;
    margin-bottom: 2rem;
  }

  .error-text {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  .error-reason {
    font-style: italic;
    margin-top: 0.5rem;
  }

  .error-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .error-help {
    color: var(--text-color-secondary, #6c757d);
    font-size: 0.9rem;
  }

  // Responsive design
  @media screen and (max-width: 576px) {
    .error-container {
      padding: 2rem 1rem;
    }

    .error-actions {
      flex-direction: column;
      align-items: center;
    }

    .error-btn {
      width: 100%;
    }

    [dir='rtl'] .error-actions {
      flex-direction: column;
    }
  }
</style>
